/**
 * LiveAvatar FULL — browser MediaRecorder → Pulse Admin archive slots
 * (same recordings/livekit/{sessionId}/… keys as Track Egress).
 *
 * Remote avatar audio must not use a bare RemoteTrack MediaRecorder (near-empty
 * WebM). Prefer captureStream() on LiveKit's audible <audio>, else AudioContext
 * tap without cloning the track.
 */
import type { Room, RemoteTrack, LocalTrack } from 'livekit-client';
import { RoomEvent, Track } from 'livekit-client';
import { api, type ArchiveSlot } from './api';
import { debugLog } from './debugLog';
import { reportClientRca } from './reportClientRca';

const TIMESLICE_MS = 2000;
/** S3 multipart requires each part except the last to be >= 5 MiB (control-api PART_SIZE_HINT). */
const PART_TARGET_BYTES = 5 * 1024 * 1024;

function pickMimeType(kind: 'audio' | 'video'): string {
  const candidates =
    kind === 'video'
      ? ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm']
      : ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) {
      return c;
    }
  }
  return kind === 'video' ? 'video/webm' : 'audio/webm';
}

function contentTypeFromMime(mime: string): string {
  const base = String(mime || '').split(';')[0].trim().toLowerCase();
  if (base === 'audio/ogg') return 'audio/ogg';
  if (base.startsWith('video/')) return 'video/webm';
  return 'audio/webm';
}

class SlotUploader {
  private sessionId: string;
  private slot: ArchiveSlot;
  private contentType: string;
  private uploadId: string | null = null;
  private key: string | null = null;
  private partNumber = 0;
  private parts: Array<{ partNumber: number; etag: string }> = [];
  private buffer: Blob[] = [];
  private bufferBytes = 0;
  private chain: Promise<void> = Promise.resolve();
  private stopped = false;

  constructor(sessionId: string, slot: ArchiveSlot, contentType: string) {
    this.sessionId = sessionId;
    this.slot = slot;
    this.contentType = contentType;
  }

  push(blob: Blob) {
    if (this.stopped || !blob || blob.size <= 0) return;
    this.buffer.push(blob);
    this.bufferBytes += blob.size;
    if (this.bufferBytes >= PART_TARGET_BYTES) {
      this.chain = this.chain.then(() => this.flushPart(false)).catch((e) => {
        reportClientRca(this.sessionId, 'archive_part_error', {
          slot: this.slot,
        }, String((e as Error)?.message || e));
      });
    }
  }

  async finish() {
    this.stopped = true;
    await this.chain;
    try {
      await this.flushPart(true);
      if (this.uploadId && !this.parts.length) {
        const err = new Error('multipart upload has no parts after finalize');
        reportClientRca(this.sessionId, 'archive_error', {
          slot: this.slot,
          phase: 'finish_empty_parts',
          uploadId: this.uploadId,
        }, err.message);
        throw err;
      }
    } catch (e) {
      debugLog('client.archive_finish_error', {
        slot: this.slot,
        error: String((e as Error)?.message || e),
      });
      void api
        .reportClientEvent(this.sessionId, {
          type: 'client.archive_error',
          message: String((e as Error)?.message || e),
          meta: { slot: this.slot },
        })
        .catch(() => undefined);
    }
  }

  private async ensureUpload() {
    if (this.uploadId) return;
    const started = await api.archiveUploadStart(this.sessionId, {
      slot: this.slot,
      contentType: this.contentType,
    });
    this.uploadId = started.uploadId;
    this.key = started.key;
    void api
      .reportClientEvent(this.sessionId, {
        type: 'client.archive_start',
        meta: { slot: this.slot, key: this.key },
      })
      .catch(() => undefined);
  }

  private async flushPart(isFinal: boolean) {
    if (!this.buffer.length) {
      if (isFinal && this.uploadId && this.parts.length) {
        await api.archiveUploadComplete(this.sessionId, this.uploadId, {
          parts: this.parts,
        });
        void api
          .reportClientEvent(this.sessionId, {
            type: 'client.archive_complete',
            meta: { slot: this.slot, key: this.key, parts: this.parts.length },
          })
          .catch(() => undefined);
      }
      return;
    }
    if (!isFinal && this.bufferBytes < PART_TARGET_BYTES) return;

    await this.ensureUpload();
    if (!this.uploadId) return;

    const blob = new Blob(this.buffer, { type: this.contentType });
    this.buffer = [];
    this.bufferBytes = 0;
    this.partNumber += 1;
    const partNumber = this.partNumber;
    const { url } = await api.archiveUploadPartUrl(this.sessionId, this.uploadId, {
      partNumber,
    });
    const res = await fetch(url, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': this.contentType },
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(
        `part PUT ${partNumber} HTTP ${res.status}${detail ? `: ${detail.slice(0, 200)}` : ''}`,
      );
    }
    const etag = (res.headers.get('ETag') || res.headers.get('etag') || '').replace(
      /"/g,
      '',
    );
    if (!etag) {
      throw new Error(`part ${partNumber} missing ETag`);
    }
    this.parts.push({ partNumber, etag });

    if (isFinal) {
      await api.archiveUploadComplete(this.sessionId, this.uploadId, {
        parts: this.parts,
      });
      void api
        .reportClientEvent(this.sessionId, {
          type: 'client.archive_complete',
          meta: { slot: this.slot, key: this.key, parts: this.parts.length },
        })
        .catch(() => undefined);
    }
  }
}

class SlotRecorder {
  readonly slot: ArchiveSlot;
  private sessionId: string;
  private recorder: MediaRecorder | null = null;
  private uploader: SlotUploader;
  private release: (() => void) | null = null;
  private bytesIn = 0;
  private startedAt = Date.now();
  private mime = '';

  constructor(
    sessionId: string,
    slot: ArchiveSlot,
    stream: MediaStream,
    release?: (() => void) | null,
  ) {
    this.sessionId = sessionId;
    this.slot = slot;
    this.release = release || null;
    const kind = slot.endsWith('Video') ? 'video' : 'audio';
    const mime = pickMimeType(kind);
    this.uploader = new SlotUploader(sessionId, slot, contentTypeFromMime(mime));
    try {
      this.recorder = new MediaRecorder(stream, { mimeType: mime });
    } catch {
      try {
        this.recorder = new MediaRecorder(stream);
      } catch (e) {
        reportClientRca(sessionId, 'archive_recorder_fail', {
          slot,
          message: String((e as Error)?.message || e),
        }, String((e as Error)?.message || e));
        this.recorder = null;
        this.release?.();
        this.release = null;
        return;
      }
    }
    this.recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) {
        this.bytesIn += ev.data.size;
        this.uploader.push(ev.data);
      }
    };
    try {
      this.recorder.start(TIMESLICE_MS);
      this.mime = this.recorder.mimeType || mime;
      reportClientRca(sessionId, 'archive_recorder_start', {
        slot,
        mime: this.mime,
      });
    } catch (e) {
      reportClientRca(sessionId, 'archive_recorder_fail', {
        slot,
        message: String((e as Error)?.message || e),
      }, String((e as Error)?.message || e));
      this.recorder = null;
      this.release?.();
      this.release = null;
    }
  }

  get active() {
    return Boolean(this.recorder && this.recorder.state !== 'inactive');
  }

  get recordedBytes() {
    return this.bytesIn;
  }

  async stop() {
    const rec = this.recorder;
    this.recorder = null;
    if (rec && rec.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        rec.addEventListener(
          'stop',
          () => {
            // Final dataavailable often fires just after stop.
            window.setTimeout(done, 600);
          },
          { once: true },
        );
        try {
          rec.requestData();
        } catch {
          /* ignore */
        }
        try {
          rec.stop();
        } catch {
          done();
        }
        window.setTimeout(done, 4000);
      });
    }
    await this.uploader.finish();
    const durationMs = Date.now() - this.startedAt;
    // Short calls used to escape the 30s gate while still storing silence (~5KB/24s).
    const bytesPerSec = durationMs > 0 ? (this.bytesIn * 1000) / durationMs : 0;
    const silentSuspect =
      this.slot === 'botAudio'
      && durationMs > 8_000
      && this.bytesIn > 0
      && (this.bytesIn < 100_000 || bytesPerSec < 12_000);
    reportClientRca(this.sessionId, 'archive_slot_summary', {
      slot: this.slot,
      mime: this.mime,
      recordedBytes: this.bytesIn,
      durationMs,
      silentSuspect: silentSuspect ? 1 : 0,
    });
    try {
      this.release?.();
    } catch {
      /* ignore */
    }
    this.release = null;
  }
}

function trackToStream(track: MediaStreamTrack): MediaStream {
  return new MediaStream([track]);
}

function mediaStreamTrackFromLk(track: LocalTrack | RemoteTrack): MediaStreamTrack | null {
  const anyTrack = track as unknown as { mediaStreamTrack?: MediaStreamTrack };
  return anyTrack.mediaStreamTrack || null;
}

/** Publisher identity + LiveKit SID for a remote audio track (for RCA). */
function remoteAudioPublisher(
  room: Room,
  track: RemoteTrack,
): { identity: string; trackSid: string } {
  const sid = String(track.sid || '');
  for (const p of room.remoteParticipants.values()) {
    for (const pub of p.audioTrackPublications.values()) {
      if (pub.track === track || (sid && pub.trackSid === sid)) {
        return {
          identity: String(p.identity || ''),
          trackSid: String(pub.trackSid || sid || ''),
        };
      }
    }
  }
  return { identity: '', trackSid: sid };
}

type RemoteAudioCandidate = {
  track: RemoteTrack;
  mst: MediaStreamTrack;
  identity: string;
  trackSid: string;
};

function listRemoteAudioCandidates(room: Room): RemoteAudioCandidate[] {
  const out: RemoteAudioCandidate[] = [];
  for (const p of room.remoteParticipants.values()) {
    for (const pub of p.audioTrackPublications.values()) {
      const tr = pub.track as RemoteTrack | undefined;
      if (!tr) continue;
      const mst = mediaStreamTrackFromLk(tr);
      if (!mst || mst.readyState !== 'live') continue;
      out.push({
        track: tr,
        mst,
        identity: String(p.identity || ''),
        trackSid: String(pub.trackSid || tr.sid || ''),
      });
    }
  }
  return out;
}

/**
 * Compact scalar for Dynamo slimMeta (no nested arrays).
 * Example: heygen:TR_x:m0|liveavatar-agent-…:TR_y:m1
 */
function remotesSummary(room: Room): string {
  return listRemoteAudioCandidates(room)
    .map((c) => `${c.identity}:${c.trackSid}:m${c.mst.muted ? 1 : 0}`)
    .join('|')
    .slice(0, 220);
}

/**
 * Prefer unmuted heygen (avatar mouth) over muted liveavatar-agent duplicates.
 * First-subscribed muted agent was writing near-empty bot.webm while heygen spoke.
 */
function scoreBotAudioCandidate(c: RemoteAudioCandidate): number {
  let score = 0;
  if (!c.mst.muted) score += 100;
  if (c.mst.enabled) score += 10;
  const id = c.identity.toLowerCase();
  if (id === 'heygen' || id.startsWith('heygen-')) score += 50;
  else if (id.includes('heygen')) score += 40;
  else if (id.includes('liveavatar-agent')) score += 5;
  else if (id.includes('liveavatar') || id.includes('avatar')) score += 20;
  return score;
}

function pickBotAudioCandidate(room: Room): RemoteAudioCandidate | null {
  const cands = listRemoteAudioCandidates(room);
  if (!cands.length) return null;
  cands.sort((a, b) => scoreBotAudioCandidate(b) - scoreBotAudioCandidate(a));
  return cands.find((c) => !c.mst.muted) || cands[0];
}

type CaptureEl = HTMLAudioElement & {
  captureStream?: () => MediaStream;
  mozCaptureStream?: () => MediaStream;
};

function findLiveKitAudioElement(mst: MediaStreamTrack): HTMLAudioElement | null {
  const nodes = [
    ...document.querySelectorAll<HTMLAudioElement>('audio[data-lk-remote-audio="1"]'),
  ];
  for (const node of nodes) {
    const so = node.srcObject;
    if (!(so instanceof MediaStream)) continue;
    if (so.getAudioTracks().some((t) => t.id === mst.id)) return node;
  }
  // Prefer audible LiveAvatar element even if track id mismatch (reattach races).
  const preferred =
    nodes.find((n) => n.dataset.lkAudioKind === 'liveavatar' && !n.muted)
    || nodes.find((n) => n.dataset.lkAudioKind === 'bot' && !n.muted)
    || nodes.find((n) => !n.muted);
  return preferred || null;
}

function captureFromElement(el: HTMLAudioElement): MediaStream | null {
  const cap = el as CaptureEl;
  const fn = cap.captureStream || cap.mozCaptureStream;
  if (typeof fn !== 'function') return null;
  try {
    const stream = fn.call(el);
    if (stream.getAudioTracks().some((t) => t.readyState === 'live')) return stream;
  } catch (e) {
    debugLog('client.archive_capture_stream_fail', {
      error: String((e as Error)?.message || e),
    });
  }
  return null;
}

/**
 * Pull remote WebRTC audio into a recordable MediaStream.
 * Bare RemoteTrack MediaRecorder often yields near-empty Opus WebM.
 */
function tapRemoteAudioForRecord(mst: MediaStreamTrack): {
  stream: MediaStream;
  release: () => void;
  method: string;
  elementMuted?: boolean;
  paused?: boolean;
} {
  const lkEl = findLiveKitAudioElement(mst);
  if (lkEl) {
    const captured = captureFromElement(lkEl);
    if (captured) {
      return {
        stream: captured,
        release: () => undefined,
        method: 'captureStream',
        elementMuted: lkEl.muted,
        paused: lkEl.paused,
      };
    }
  }

  // Do not clone — clones of remote tracks often stay silent for MediaRecorder.
  const inbound = new MediaStream([mst]);
  const el = document.createElement('audio');
  el.autoplay = true;
  el.muted = true;
  el.volume = 0;
  el.setAttribute('playsinline', 'true');
  el.dataset.pulseClientArchive = 'botAudio';
  el.srcObject = inbound;
  document.body.appendChild(el);
  void el.play().catch(() => undefined);

  let stream: MediaStream = inbound;
  let ctx: AudioContext | null = null;
  let method = 'mediaStreamDirect';
  try {
    const AC =
      window.AudioContext
      || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AC) {
      ctx = new AC();
      const src = ctx.createMediaStreamSource(inbound);
      const dest = ctx.createMediaStreamDestination();
      const keepAlive = ctx.createGain();
      keepAlive.gain.value = 0;
      src.connect(dest);
      src.connect(keepAlive);
      keepAlive.connect(ctx.destination);
      stream = dest.stream;
      method = 'audioContext';
      void ctx.resume().catch(() => undefined);
    }
  } catch (e) {
    debugLog('client.archive_audio_tap_fallback', {
      error: String((e as Error)?.message || e),
    });
  }

  return {
    stream,
    method,
    elementMuted: true,
    paused: el.paused,
    release: () => {
      try {
        el.pause();
        el.srcObject = null;
        el.remove();
      } catch {
        /* ignore */
      }
      try {
        void ctx?.close();
      } catch {
        /* ignore */
      }
    },
  };
}

export type ClientSessionArchiveHandle = {
  stop: () => Promise<void>;
};

type VideoPublicationLike = {
  track?: LocalTrack | null;
  isMuted?: boolean;
  isEnabled?: boolean;
};

/** True when the user is actively sending camera frames (not haircheck-off / disabled track). */
export function shouldRecordUserVideo(
  mediaStream: MediaStream | null | undefined,
  videoPublications: Iterable<VideoPublicationLike>,
): boolean {
  for (const track of mediaStream?.getVideoTracks() ?? []) {
    if (track.readyState === 'live' && track.enabled) return true;
  }
  for (const pub of videoPublications) {
    if (pub.isMuted || pub.isEnabled === false) continue;
    const mst = pub.track?.mediaStreamTrack;
    if (mst && mst.readyState === 'live' && mst.enabled) return true;
  }
  return false;
}

function localVideoTrackForArchive(
  mediaStream: MediaStream | null | undefined,
  room: Room,
): MediaStreamTrack | null {
  if (!shouldRecordUserVideo(mediaStream, room.localParticipant.videoTrackPublications.values())) {
    return null;
  }
  const fromHaircheck = mediaStream?.getVideoTracks().find(
    (t) => t.readyState === 'live' && t.enabled,
  );
  if (fromHaircheck) return fromHaircheck;
  const pub = [...room.localParticipant.videoTrackPublications.values()].find(
    (p) => p.track && !p.isMuted && p.isEnabled !== false,
  );
  if (!pub?.track) return null;
  return mediaStreamTrackFromLk(pub.track as LocalTrack);
}

/**
 * Start up to four slot recorders for a FULL LaRoom session.
 * Fail-soft: never throws to the caller.
 */
export function startClientSessionArchive(opts: {
  sessionId: string;
  room: Room;
  mediaStream?: MediaStream | null;
}): ClientSessionArchiveHandle {
  const { sessionId, room, mediaStream } = opts;
  const slots = new Map<ArchiveSlot, SlotRecorder>();
  let botAudioArming = false;

  const startSlot = (
    slot: ArchiveSlot,
    stream: MediaStream | null,
    release?: (() => void) | null,
  ) => {
    if (!stream || slots.has(slot)) {
      release?.();
      return;
    }
    const tracks = stream.getTracks().filter((t) => t.readyState === 'live');
    if (!tracks.length) {
      release?.();
      return;
    }
    const rec = new SlotRecorder(sessionId, slot, new MediaStream(tracks), release);
    if (rec.active) slots.set(slot, rec);
    else release?.();
  };

  const localAudio =
    mediaStream?.getAudioTracks()[0]
    || mediaStreamTrackFromLk(
      [...room.localParticipant.audioTrackPublications.values()][0]?.track as LocalTrack,
    );
  if (localAudio) startSlot('userAudio', trackToStream(localAudio));
  const localVideo = localVideoTrackForArchive(mediaStream, room);
  if (localVideo) startSlot('userVideo', trackToStream(localVideo));

  let botAudioArmMeta: {
    trackSid: string;
    identity: string;
    mstId: string;
  } | null = null;
  let botAudioWaitTimer: number | undefined;
  const botAudioUnmuteCleanups: Array<() => void> = [];

  const clearBotAudioWait = () => {
    if (botAudioWaitTimer != null) {
      window.clearTimeout(botAudioWaitTimer);
      botAudioWaitTimer = undefined;
    }
    while (botAudioUnmuteCleanups.length) {
      try {
        botAudioUnmuteCleanups.pop()?.();
      } catch {
        /* ignore */
      }
    }
  };

  const startBotAudioCapture = (candidate: RemoteAudioCandidate, why: string) => {
    if (slots.has('botAudio') || botAudioArming) return;
    botAudioArming = true;
    clearBotAudioWait();
    const { track, mst } = candidate;
    const publisher = remoteAudioPublisher(room, track);
    const summary = remotesSummary(room);
    const remoteAudioCount = listRemoteAudioCandidates(room).length;

    const finishArm = (armWhy: string) => {
      if (slots.has('botAudio')) {
        botAudioArming = false;
        return;
      }
      const tapped = tapRemoteAudioForRecord(mst);
      botAudioArmMeta = {
        trackSid: publisher.trackSid || candidate.trackSid,
        identity: publisher.identity || candidate.identity,
        mstId: mst.id,
      };
      const tapMeta = {
        slot: 'botAudio',
        method: tapped.method,
        trackId: mst.id,
        trackSid: botAudioArmMeta.trackSid,
        identity: botAudioArmMeta.identity,
        trackMuted: mst.muted ? 1 : 0,
        trackEnabled: mst.enabled ? 1 : 0,
        trackReadyState: mst.readyState,
        elementMuted: tapped.elementMuted ? 1 : 0,
        paused: tapped.paused ? 1 : 0,
        remoteAudioCount,
        remotesSummary: summary,
        why: armWhy,
      };
      reportClientRca(sessionId, 'archive_audio_tap', tapMeta);
      reportClientRca(sessionId, 'archive_bot_audio_arm', {
        slot: 'botAudio',
        why: armWhy,
        method: tapped.method,
        trackId: mst.id,
        trackSid: botAudioArmMeta.trackSid,
        identity: botAudioArmMeta.identity,
        trackMuted: mst.muted ? 1 : 0,
        remoteAudioCount,
        remotesSummary: summary,
      });
      startSlot('botAudio', tapped.stream, tapped.release);
      botAudioArming = false;
    };

    const lkEl = findLiveKitAudioElement(mst);
    if (lkEl && !lkEl.paused && !lkEl.muted) {
      finishArm(why);
      return;
    }
    if (lkEl) {
      const onPlaying = () => finishArm(`${why}+playing`);
      lkEl.addEventListener('playing', onPlaying, { once: true });
      window.setTimeout(() => {
        lkEl.removeEventListener('playing', onPlaying);
        if (!slots.has('botAudio')) finishArm(`${why}+timeout`);
      }, 2500);
      return;
    }
    window.setTimeout(() => {
      if (!slots.has('botAudio')) finishArm(`${why}+deferred`);
    }, 500);
  };

  /**
   * Pick best remote (unmuted heygen over muted agent). If all muted, wait for
   * unmute instead of first-win locking onto a silent captureStream.
   */
  const armBestBotAudio = (why: string) => {
    if (slots.has('botAudio') || botAudioArming) return;
    const best = pickBotAudioCandidate(room);
    if (!best) return;

    if (!best.mst.muted) {
      startBotAudioCapture(best, why);
      return;
    }

    if (botAudioWaitTimer != null) return;
    const summary = remotesSummary(room);
    reportClientRca(sessionId, 'archive_bot_audio_wait', {
      slot: 'botAudio',
      why,
      identity: best.identity,
      trackSid: best.trackSid,
      trackId: best.mst.id,
      trackMuted: 1,
      remoteAudioCount: listRemoteAudioCandidates(room).length,
      remotesSummary: summary,
    });
    for (const c of listRemoteAudioCandidates(room)) {
      const onUnmute = () => {
        clearBotAudioWait();
        armBestBotAudio('unmute');
      };
      c.mst.addEventListener('unmute', onUnmute);
      botAudioUnmuteCleanups.push(() => c.mst.removeEventListener('unmute', onUnmute));
    }
    botAudioWaitTimer = window.setTimeout(() => {
      botAudioWaitTimer = undefined;
      clearBotAudioWait();
      if (slots.has('botAudio') || botAudioArming) return;
      const again = pickBotAudioCandidate(room);
      if (!again) return;
      // Prefer any unmuted after the wait; otherwise last-resort arm (still RCA'd).
      const pick =
        listRemoteAudioCandidates(room).find((c) => !c.mst.muted) || again;
      startBotAudioCapture(pick, 'unmute_timeout');
    }, 3000);
  };

  const attachRemote = (track: RemoteTrack) => {
    const mst = mediaStreamTrackFromLk(track);
    if (!mst) return;
    if (track.kind === Track.Kind.Video) {
      startSlot('botVideo', trackToStream(mst));
      return;
    }
    if (track.kind === Track.Kind.Audio) {
      armBestBotAudio('subscribed');
    }
  };

  for (const p of room.remoteParticipants.values()) {
    for (const pub of p.trackPublications.values()) {
      if (pub.track) attachRemote(pub.track as RemoteTrack);
    }
  }

  const onSubscribed = (track: RemoteTrack) => {
    attachRemote(track);
  };
  room.on(RoomEvent.TrackSubscribed, onSubscribed);

  let progressTicks = 0;
  let progressReported = false;
  const pollId = window.setInterval(() => {
    progressTicks += 1;
    if (!slots.has('userAudio')) {
      const t =
        mediaStream?.getAudioTracks()[0]
        || mediaStreamTrackFromLk(
          [...room.localParticipant.audioTrackPublications.values()][0]?.track as LocalTrack,
        );
      if (t) startSlot('userAudio', trackToStream(t));
    }
    if (!slots.has('userVideo')) {
      const t = localVideoTrackForArchive(mediaStream, room);
      if (t) startSlot('userVideo', trackToStream(t));
    }
    if (!slots.has('botAudio')) {
      armBestBotAudio('poll');
    } else if (!progressReported && progressTicks >= 8) {
      // ~16s into the call — mid-call byte snapshot for silent-botAudio RCA.
      const bot = slots.get('botAudio');
      if (bot) {
        progressReported = true;
        reportClientRca(sessionId, 'archive_slot_progress', {
          slot: 'botAudio',
          recordedBytes: bot.recordedBytes,
          silentSuspect: bot.recordedBytes > 0 && bot.recordedBytes < 80_000 ? 1 : 0,
          slotsArmed: slots.size,
          trackSid: botAudioArmMeta?.trackSid || '',
          identity: botAudioArmMeta?.identity || '',
          trackId: botAudioArmMeta?.mstId || '',
          remoteAudioCount: listRemoteAudioCandidates(room).length,
          remotesSummary: remotesSummary(room),
        });
      }
    }
    if (!slots.has('botVideo')) {
      for (const p of room.remoteParticipants.values()) {
        for (const pub of p.videoTrackPublications.values()) {
          if (pub.track) {
            attachRemote(pub.track as RemoteTrack);
            break;
          }
        }
        if (slots.has('botVideo')) break;
      }
    }
  }, 2000);

  let stopped = false;
  return {
    stop: async () => {
      if (stopped) return;
      stopped = true;
      window.clearInterval(pollId);
      clearBotAudioWait();
      try {
        room.off(RoomEvent.TrackSubscribed, onSubscribed);
      } catch {
        /* ignore */
      }
      const list = [...slots.values()];
      const armed = list.map((r) => r.slot).join(',');
      slots.clear();
      await Promise.allSettled(list.map((r) => r.stop()));
      reportClientRca(sessionId, 'archive_stopped', {
        slotsArmed: list.length,
        reason: armed || 'none',
      });
    },
  };
}
